import type { Dirent } from 'node:fs';
import { readFile, readdir, stat } from 'node:fs/promises';
import { join } from 'node:path';

import * as p from '@clack/prompts';
import pc from 'picocolors';
import type { DatabaseKind } from './db/platform-api.js';
import { DB_ENV_VAR_NAMES } from './db/platform-api.js';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export type PreflightIssueCode = 'MISSING_ENV_VAR' | 'LOCALHOST_ENV_VAR' | 'LOCAL_STORAGE_PATH';

/**
 * Structured hint describing how deploy can offer to auto-fix an issue
 * before it becomes a blocking error. Consumed by
 * `deploy/auto-provision-database.ts` when running in an interactive TTY.
 */
export type PreflightAutofix = {
  kind: 'create-managed-database';
  provider: DatabaseKind;
  envVarName: string;
};

export interface PreflightIssue {
  code: PreflightIssueCode;
  severity: 'error' | 'warning';
  message: string;
  /**
   * Remediation. A single string renders as one arrow line; an array renders
   * as one arrow line per entry so multi-step fixes (run this command, OR set
   * this env var) stay legible instead of collapsing into a wall of text.
   */
  fix: string | string[];
  autofix?: PreflightAutofix;
}

/* ------------------------------------------------------------------ */
/*  Config                                                            */
/* ------------------------------------------------------------------ */

/**
 * Env vars the runtime/platform sets automatically — referencing these in
 * user code is fine even when not present in the user's `.env` file.
 */
const ENV_VAR_ALLOWLIST_EXACT = new Set([
  'PORT',
  'HOST',
  'HOSTNAME',
  'NODE_ENV',
  'NODE_OPTIONS',
  'PWD',
  'HOME',
  'USER',
  'PATH',
  'TZ',
  'LANG',
  'CI',
  // Framework/tooling sentinel flags read by bundled dependencies (debug,
  // pino, @mastra/* internals). Referencing these from the bundle is
  // expected and shouldn't be surfaced as a missing-env-var warning.
  'DEBUG',
  'DEBUG_FD',
  'DEBUG_COLORS',
  'DEBUG_DEPTH',
  'DEBUG_HIDE_DATE',
  'NO_COLOR',
  'FORCE_COLOR',
  'EXPERIMENTAL_FEATURES',
  'SKILLS_BASE_DIR',
  'AUTO_BLOCK_EXTERNAL_PROVIDERS',
]);

/**
 * Prefixes for env vars set by the platform, runtime, or tooling.
 */
const ENV_VAR_ALLOWLIST_PREFIXES = [
  'MASTRA_',
  'npm_',
  'OTEL_',
  'NEXT_',
  'VERCEL_',
  'AWS_LAMBDA_',
  // Observational memory internal flags
  'OM_',
];

/**
 * Metadata emitted by the `mastra-local-storage-detector` Rollup plugin
 * during bundling.  Each entry represents a host-local URL found in a
 * *user* module (node_modules are excluded) that survived tree-shaking.
 */
interface LocalStorageDetection {
  value: string;
  hint: string;
  module: string;
  /**
   * Env var that guards this literal at runtime (the literal is the fallback
   * arm of a `process.env.X || literal` expression). Only present in
   * `preflight-metadata.json` — the legacy file never carries it.
   */
  guardedBy?: string;
}

/**
 * Unified metadata emitted by newer deployers as `preflight-metadata.json`.
 * `userEnvRefs` lists the env vars referenced from *user* modules only, so
 * the missing-env-var check doesn't warn about vars read by bundled library
 * code the project never references.
 */
interface PreflightMetadata {
  version: number;
  localPaths: LocalStorageDetection[];
  userEnvRefs: string[];
}

/** Legacy metadata file emitted by older deployers (and still emitted by newer ones). */
const LOCAL_PATHS_METADATA_FILE = 'preflight-local-paths.json';

/** Unified metadata file emitted by newer deployers. */
const PREFLIGHT_METADATA_FILE = 'preflight-metadata.json';

/**
 * Statically-extracted `backgroundTasks` manifest emitted by newer deployers.
 * When present with `enabled: true`, the deployed API needs a `REDIS_URL`
 * so the platform can spin up a worker service alongside it — the worker
 * shares the API's `REDIS_URL` for job coordination.
 */
const WORKERS_MANIFEST_FILE = 'workers.json';

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/**
 * Inspect a built `.mastra/output` directory plus the env vars about to be
 * uploaded and return a list of issues that are likely to cause the deploy
 * to fail with a USER-attributable error.
 *
 * Returns an empty array when no build output is found — the caller is
 * responsible for surfacing the missing-output error.
 */
export async function preflightBuildOutput(
  targetDir: string,
  envVars: Record<string, string>,
  options: {
    /**
     * Whether the CLI has the full env picture for this deploy (an explicit
     * `--env-file` or an ambient `.env*` file). When false, env vars may be
     * stored on the platform and invisible to the CLI, so env-guarded local
     * paths are surfaced as warnings instead of errors. Defaults to true.
     */
    hasEnvFile?: boolean;
    /**
     * Env var names the platform injects at deploy time (e.g. TURSO_DATABASE_URL
     * from an attached managed database) — names only, values are platform-side
     * secrets. Three states:
     * - `string[]` — platform env context fetched and the field was present:
     *   the env picture is complete, so guarded local paths whose var is
     *   neither provided nor managed are trustworthy hard errors.
     * - `null` — platform env context fetched but the field was absent (older
     *   platform, or an endpoint that doesn't expose names): the picture is
     *   incomplete, so guarded misses soften to warnings.
     * - `undefined` — no platform context at all (lint, studio deploy):
     *   severity falls back to `hasEnvFile`.
     */
    managedEnvVarNames?: string[] | null;
    /**
     * User-facing name of the environment being deployed to (`production`,
     * `staging`, etc.) — threaded into remediation text so the printed
     * `mastra env db create <env> --kind ...` reads like a command a human
     * would type. NOT the slug: on some platforms the production env's slug
     * is derived from the project name (e.g. `my-app-xyz-1234`), which the
     * platform's env-resolver accepts but is jarring to see printed back.
     * The env-resolver accepts id, name, or slug, so name is safe.
     * Omit for lint / studio contexts.
     */
    environmentName?: string;
    /**
     * Whether this deploy path can provision a dedicated worker service from
     * the build's `workers.json` manifest. Only the unified `mastra deploy`
     * (environment) flow passes true — legacy `studio deploy`/`server deploy`
     * strip the manifest from their artifacts and run workers in-process, so
     * surfacing a workers-need-REDIS_URL issue there would be noise.
     * Defaults to false.
     */
    checkWorkers?: boolean;
  } = {},
): Promise<PreflightIssue[]> {
  const { hasEnvFile = true, managedEnvVarNames, environmentName, checkWorkers = false } = options;
  const outputDir = join(targetDir, '.mastra', 'output');
  const entryPath = join(outputDir, 'index.mjs');

  // If there's no build output yet, there's nothing to check. The deploy
  // command verifies the entry exists separately.
  try {
    await stat(entryPath);
  } catch {
    return [];
  }

  // Unified metadata from newer deployers. Absent for stale builds or older
  // deployers — each check falls back to its previous behavior.
  const metadata = await readPreflightMetadata(outputDir);

  const issues: PreflightIssue[] = [];

  if (metadata) {
    // User modules' env refs were captured structurally at build time, so
    // library-only refs inside the bundle never produce warnings.
    issues.push(...checkEnvVarNames(metadata.userEnvRefs, envVars, managedEnvVarNames));
  } else {
    const bundleSources = await readBundleSources(outputDir);
    const combinedSource = bundleSources.join('\n');
    issues.push(...checkMissingEnvVars(combinedSource, envVars, managedEnvVarNames));
  }

  // LOCAL_STORAGE_PATH — read from bundler-generated metadata.  The Rollup
  // plugin `mastra-local-storage-detector` runs during bundling and only
  // reports paths from user modules (not node_modules) that survived
  // tree-shaking, so library examples are structurally excluded.
  issues.push(
    ...(await checkLocalStoragePaths(outputDir, metadata, envVars, hasEnvFile, managedEnvVarNames, environmentName)),
  );

  // Background workers need a REDIS_URL to coordinate with the API service.
  // If the extracted manifest says workers are enabled but no REDIS_URL is
  // in scope, surface a missing-env-var issue with the same `redis` autofix
  // used elsewhere so `maybeAutoProvisionDatabases` can offer inline attach.
  // Opt-in: only the unified deploy flow provisions workers.
  if (checkWorkers) {
    issues.push(...(await checkWorkersNeedRedis(outputDir, envVars, managedEnvVarNames)));
  }

  return issues;
}

/**
 * Mirror the platform's deploy-time env merge: local/request env vars are
 * applied over the vars already stored on the target environment or server
 * project (request wins). Preflight should see this merged picture so vars
 * stored only on the platform don't produce false MISSING_ENV_VAR /
 * LOCAL_STORAGE_PATH alarms.
 */
export function mergePreflightEnvVars(
  stored: Record<string, string> | null | undefined,
  local: Record<string, string>,
): Record<string, string> {
  return { ...stored, ...local };
}

export type PreflightOutcome = 'ok' | 'blocked' | 'cancelled';

/**
 * Print preflight issues and decide whether the deploy should proceed.
 *
 * Returns:
 * - `'ok'`     — no issues, or warnings the caller has accepted.
 * - `'blocked'`— at least one error-severity issue. Errors always block,
 *                regardless of `autoAccept` / headless mode. Caller should
 *                exit non-zero so CI surfaces the failure.
 * - `'cancelled'` — warnings only, but the user explicitly declined the
 *                confirmation prompt. Caller should exit zero (normal
 *                user-initiated cancel).
 *
 * `--skip-preflight` is the escape hatch when a check is a false positive.
 */
export async function printPreflightIssues(
  issues: PreflightIssue[],
  options: { autoAccept: boolean },
): Promise<PreflightOutcome> {
  if (issues.length === 0) return 'ok';

  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');

  const renderFix = (fix: string | string[]): string => {
    const steps = Array.isArray(fix) ? fix : [fix];
    return steps.map(step => `  ${pc.dim('→')} ${step}`).join('\n');
  };

  for (const issue of warnings) {
    p.log.warn(`${pc.yellow(`[${issue.code}]`)} ${issue.message}\n${renderFix(issue.fix)}`);
  }

  for (const issue of errors) {
    p.log.error(`${pc.red(`[${issue.code}]`)} ${issue.message}\n${renderFix(issue.fix)}`);
  }

  if (errors.length > 0) {
    p.log.error(
      `Deploy blocked by ${errors.length} preflight error(s). ` +
        `Fix the issues above, or pass --skip-preflight to override.`,
    );
    return 'blocked';
  }

  // Warnings only.
  if (options.autoAccept) return 'ok';

  const confirmed = await p.confirm({
    message: `Found ${warnings.length} preflight warning(s). Deploy anyway?`,
    initialValue: true,
  });

  if (p.isCancel(confirmed) || !confirmed) {
    return 'cancelled';
  }

  return 'ok';
}

/* ------------------------------------------------------------------ */
/*  Bundle reading                                                    */
/* ------------------------------------------------------------------ */

async function readBundleSources(outputDir: string): Promise<string[]> {
  const files = await collectMjsFiles(outputDir);
  const contents = await Promise.all(files.map(f => readFile(f, 'utf-8').catch(() => '')));
  return contents;
}

async function collectMjsFiles(dir: string): Promise<string[]> {
  const out: string[] = [];
  let entries: Array<Dirent | string>;
  try {
    entries = (await readdir(dir, { withFileTypes: true })) as Array<Dirent | string>;
  } catch {
    return out;
  }
  for (const entry of entries) {
    const name = typeof entry === 'string' ? entry : entry.name;
    if (name === 'node_modules') continue;
    const full = join(dir, name);
    const isDir = typeof entry === 'string' ? false : entry.isDirectory?.() === true;
    const isFile = typeof entry === 'string' ? true : entry.isFile?.() === true;
    if (isDir) {
      out.push(...(await collectMjsFiles(full)));
    } else if (isFile && (name.endsWith('.mjs') || name.endsWith('.js'))) {
      out.push(full);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Check 1 — missing env vars                                        */
/* ------------------------------------------------------------------ */

const PROCESS_ENV_REGEX = /\bprocess\.env\.([A-Z_][A-Z0-9_]*)\b/g;
const PROCESS_ENV_BRACKET_REGEX = /\bprocess\.env\[['"]([A-Z_][A-Z0-9_]*)['"]\]/g;

/**
 * Fallback for builds without `preflight-metadata.json`: regex-scan the whole
 * bundle (user + library code) for `process.env.X` references.
 */
function checkMissingEnvVars(
  source: string,
  envVars: Record<string, string>,
  managedEnvVarNames?: string[] | null,
): PreflightIssue[] {
  const referenced = new Set<string>();
  for (const match of source.matchAll(PROCESS_ENV_REGEX)) {
    referenced.add(match[1]!);
  }
  for (const match of source.matchAll(PROCESS_ENV_BRACKET_REGEX)) {
    referenced.add(match[1]!);
  }

  return checkEnvVarNames([...referenced], envVars, managedEnvVarNames);
}

/** Env vars the platform/runtime sets automatically at deploy time. */
function isPlatformProvidedEnvVar(name: string): boolean {
  return ENV_VAR_ALLOWLIST_EXACT.has(name) || ENV_VAR_ALLOWLIST_PREFIXES.some(prefix => name.startsWith(prefix));
}

/**
 * True when a connection-string value points at the local machine
 * (`localhost`, `127.0.0.1`, `::1`, `0.0.0.0`). Such values work in local dev
 * but can never be reached from the deployed server, so preflight treats a
 * provider-known env var carrying one as effectively unusable. Values that
 * don't parse as URLs are left alone — we only flag what we can read.
 */
export function isLocalhostUrl(value: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(value).hostname;
  } catch {
    return false;
  }
  // URL wraps IPv6 hostnames in brackets ("[::1]").
  const host = hostname.replace(/^\[|\]$/g, '');
  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '::1' ||
    host === '0.0.0.0' ||
    host.startsWith('127.')
  );
}

/**
 * The host (hostname + port) of a localhost URL, safe to echo in warnings.
 * Never returns credentials — connection strings can carry passwords and
 * preflight output lands in CI logs. Only called on values that already
 * passed {@link isLocalhostUrl}, so the URL parse cannot fail.
 */
function localhostHostOf(value: string): string {
  return new URL(value).host;
}

function isUsableEnvVarValue(name: string, value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (!dbAutofixFor(name)) return true;

  try {
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
}

function checkEnvVarNames(
  referenced: Iterable<string>,
  envVars: Record<string, string>,
  managedEnvVarNames?: string[] | null,
): PreflightIssue[] {
  const provided = new Set(
    Object.entries(envVars)
      .filter(([name, value]) => isUsableEnvVarValue(name, value))
      .map(([name]) => name),
  );
  const managed = new Set(managedEnvVarNames ?? []);
  const missing: string[] = [];
  const issues: PreflightIssue[] = [];

  for (const name of new Set(referenced)) {
    if (provided.has(name)) {
      // Present but pointing at the local machine: the value works in dev
      // but can't be reached from the deployed server. Only flagged for
      // provider-known vars (where we can offer a managed replacement) and
      // only when no managed database already injects the var at deploy
      // time (managed values win the platform's env merge, so a localhost
      // value in the env file is then harmless).
      const autofix = dbAutofixFor(name);
      if (autofix && !managed.has(name) && isLocalhostUrl(envVars[name]!)) {
        issues.push({
          code: 'LOCALHOST_ENV_VAR',
          severity: 'warning',
          // Only the host is echoed — connection URLs can carry credentials,
          // and preflight warnings end up in CI logs.
          message: `${name} in the env file being deployed points at localhost (${localhostHostOf(envVars[name]!)}) — the deployed server won't be able to reach it.`,
          fix: SELF_SERVE_DB_KINDS.has(autofix.provider)
            ? `Point ${name} at a hosted ${autofix.provider} instance, or let \`mastra deploy\` provision a managed ${autofix.provider} for this environment.`
            : `Point ${name} at a hosted ${autofix.provider} instance.`,
          autofix,
        });
      }
      continue;
    }
    if (managed.has(name)) continue;
    if (isPlatformProvidedEnvVar(name)) continue;
    missing.push(name);
  }

  if (missing.length === 0) return issues;

  missing.sort();

  // Split provider-known env vars into their own MISSING_ENV_VAR issues so we
  // can attach an autofix hint (`create-managed-database`) — the deploy command
  // then offers inline provisioning. Everything else stays in the single
  // aggregated text warning.
  const unprovisioned: string[] = [];
  for (const name of missing) {
    const autofix = dbAutofixFor(name);
    if (autofix) {
      issues.push({
        code: 'MISSING_ENV_VAR',
        severity: 'warning',
        message: `Build references ${name} but the env file being deployed does not provide it.`,
        fix: SELF_SERVE_DB_KINDS.has(autofix.provider)
          ? `Add ${name} to your env file, or let \`mastra deploy\` provision a managed ${autofix.provider} for this environment.`
          : `Add ${name} to your env file.`,
        autofix,
      });
    } else {
      unprovisioned.push(name);
    }
  }

  if (unprovisioned.length > 0) {
    issues.push({
      code: 'MISSING_ENV_VAR',
      severity: 'warning',
      message: `Build references ${unprovisioned.length} env var(s) not in the env file being deployed: ${unprovisioned.join(', ')}`,
      fix: `Add them to your env file, or confirm your code provides a fallback (e.g. \`process.env.X ?? 'default'\`).`,
    });
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Check 3 — workers need REDIS_URL                                  */
/* ------------------------------------------------------------------ */

/**
 * Whether the deploy env satisfies the Redis requirement for a dedicated
 * workers service: the platform needs Redis (pub/sub) to coordinate the
 * worker service with the API. Met when a usable `REDIS_URL` is in the
 * deploy env or provided by a platform-managed database.
 */
export function hasWorkersRedisRequirement(
  envVars: Record<string, string>,
  managedEnvVarNames?: string[] | null,
): boolean {
  const redisUrl = envVars.REDIS_URL;
  const managed = new Set(managedEnvVarNames ?? []);
  return (redisUrl !== undefined && isUsableEnvVarValue('REDIS_URL', redisUrl)) || managed.has('REDIS_URL');
}

/**
 * If the build extracted a workers manifest with `enabled: true` but the
 * deploy env doesn't provide `REDIS_URL` (locally or via a platform-managed
 * database), surface a missing-env-var warning with the standard `redis`
 * autofix. `maybeAutoProvisionDatabases` then offers inline attach so the
 * managed Redis exists before the platform tries to spin up a worker
 * service against the environment.
 *
 * Best-effort: the manifest file is absent for stale builds or older
 * deployers, in which case the check is skipped (falls through to the
 * existing `MISSING_ENV_VAR` path if user code references `REDIS_URL`).
 */
async function checkWorkersNeedRedis(
  outputDir: string,
  envVars: Record<string, string>,
  managedEnvVarNames?: string[] | null,
): Promise<PreflightIssue[]> {
  let raw: string;
  try {
    raw = await readFile(join(outputDir, WORKERS_MANIFEST_FILE), 'utf-8');
  } catch {
    return [];
  }

  let manifest: unknown;
  try {
    manifest = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!manifest || typeof manifest !== 'object') return [];

  const workerManifest = manifest as {
    version?: unknown;
    enabled?: unknown;
    orchestration?: { enabled?: unknown };
    scheduler?: { enabled?: unknown };
    backgroundTasks?: { enabled?: unknown };
    custom?: unknown;
  };
  const workersEnabled =
    workerManifest.version === 1
      ? workerManifest.orchestration?.enabled === true ||
        workerManifest.scheduler?.enabled === true ||
        workerManifest.backgroundTasks?.enabled === true ||
        (Array.isArray(workerManifest.custom) && workerManifest.custom.length > 0)
      : workerManifest.enabled === true;
  if (!workersEnabled) return [];

  if (hasWorkersRedisRequirement(envVars, managedEnvVarNames)) return [];

  const autofix = dbAutofixFor('REDIS_URL');
  return [
    {
      code: 'MISSING_ENV_VAR',
      severity: 'warning',
      message:
        'Background tasks are enabled in this project, but the deploy env has no REDIS_URL — the platform needs Redis to coordinate the worker service with the API.',
      fix: 'Add REDIS_URL to your env file.',
      autofix,
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Check 2 — local storage paths (bundler-generated metadata)        */
/* ------------------------------------------------------------------ */

/**
 * Read the unified `preflight-metadata.json` emitted by newer deployers.
 * Returns null when absent or malformed (stale build / older deployer).
 */
async function readPreflightMetadata(outputDir: string): Promise<PreflightMetadata | null> {
  try {
    const raw = await readFile(join(outputDir, PREFLIGHT_METADATA_FILE), 'utf-8');
    const parsed = JSON.parse(raw) as PreflightMetadata;
    if (parsed.version !== 1 || !Array.isArray(parsed.localPaths) || !Array.isArray(parsed.userEnvRefs)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Check detections written by the `mastra-local-storage-detector` Rollup
 * plugin.  Prefers the unified metadata (which carries `guardedBy` env
 * context); falls back to the legacy `preflight-local-paths.json`.  If both
 * are absent (e.g. older build, or the plugin wasn't active) the check is
 * silently skipped — no false positives.
 */
/**
 * The exact command that unblocks a missing database env var. Kind-specific
 * when the guarded var maps to a known provider (issue 35-B: the remediation
 * previously said "attach a managed database" without ever naming the command).
 */
export function dbCreateCommandFor(envVarName: string, environmentName?: string): string {
  // env name goes BEFORE flags because it's a positional argument on
  // `mastra env db create`, not a flag. Scoping to the target environment
  // matters after 8816f47: `mastra env db create` with no arg errors in
  // non-interactive shells when multiple environments exist, which is
  // exactly where preflight failures land (CI).
  //
  // We use the environment NAME (`production`, `staging`), not the platform
  // slug. On some platforms the production env's slug is derived from the
  // project name (e.g. `my-app-xyz-1234`) — technically accepted by the
  // env-resolver (which matches id | name | slug), but jarring to see
  // printed back and awkward to type. The name is what the user thinks of
  // as the environment identifier, so that's what we print.
  const envArg = environmentName ? ` ${environmentName}` : '';
  for (const [kind, names] of Object.entries(DB_ENV_VAR_NAMES)) {
    if (names.includes(envVarName)) return `mastra env db create${envArg} --kind ${kind}`;
  }
  return `mastra env db create${envArg}`;
}

/**
 * If `envVarName` is injected by a known managed database provider, return
 * the structured autofix hint deploy uses to offer inline provisioning.
 * Returns undefined for env vars that don't map to a provider — those still
 * get a text-only fix.
 */
/**
 * Kinds users can self-serve today via `mastra env db create`. Managed redis
 * exists behind a platform feature flag but isn't released yet, so printed
 * remediation text must not advertise it. The structured `redis` autofix is
 * still emitted: `maybeAutoProvisionDatabases` consults the platform's
 * per-org provider catalog before offering it, so gated orgs never see it.
 */
export const SELF_SERVE_DB_KINDS: ReadonlySet<DatabaseKind> = new Set(['turso', 'neon']);

export function dbAutofixFor(envVarName: string): PreflightAutofix | undefined {
  for (const [kind, names] of Object.entries(DB_ENV_VAR_NAMES) as [DatabaseKind, string[]][]) {
    if (names.includes(envVarName)) {
      return { kind: 'create-managed-database', provider: kind, envVarName };
    }
  }
  return undefined;
}

async function checkLocalStoragePaths(
  outputDir: string,
  metadata: PreflightMetadata | null,
  envVars: Record<string, string>,
  hasEnvFile: boolean,
  managedEnvVarNames?: string[] | null,
  environmentName?: string,
): Promise<PreflightIssue[]> {
  let detections: LocalStorageDetection[];
  if (metadata) {
    detections = metadata.localPaths;
  } else {
    try {
      const raw = await readFile(join(outputDir, LOCAL_PATHS_METADATA_FILE), 'utf-8');
      detections = JSON.parse(raw) as LocalStorageDetection[];
    } catch {
      return [];
    }
  }

  if (!Array.isArray(detections) || detections.length === 0) return [];

  const issues: PreflightIssue[] = [];

  for (const d of detections) {
    if (!d.guardedBy) {
      issues.push({
        code: 'LOCAL_STORAGE_PATH',
        severity: 'error',
        message: `Build contains a host-local storage URL: ${truncate(d.value, 80)} (${d.hint})`,
        fix: `Replace it with a hosted URL (e.g. a Turso \`libsql://...\` URL or a public Postgres connection string) and store it in your env file.`,
      });
      continue;
    }

    // Guards on vars the platform/runtime sets automatically (e.g.
    // MASTRA_STORAGE_URL on Mastra Cloud) are trusted the same way the
    // missing-env-var check trusts them — the guard is satisfied at runtime
    // even though the var never appears in a local env file.
    if (isPlatformProvidedEnvVar(d.guardedBy)) continue;

    // The literal is a dead fallback when the guarding env var is set in the
    // deploy environment. An empty value doesn't count: `process.env.X || 'file:...'`
    // still takes the fallback at runtime when X is blank.
    if (envVars[d.guardedBy]) continue;

    // Managed platform resources (e.g. an attached Turso database) inject
    // their vars at deploy time; the platform exposes the names so preflight
    // knows the guard is satisfied even though the value is invisible here.
    if (managedEnvVarNames?.includes(d.guardedBy)) continue;

    if (managedEnvVarNames !== undefined) {
      if (managedEnvVarNames === null) {
        // Platform context was fetched but didn't expose managed names (older
        // platform, or the server-project env endpoint which doesn't carry
        // them yet) — the env picture is incomplete, so don't hard-block.
        // TODO(managed-env-names): once every platform env endpoint exposes
        // managedEnvVarNames, drop this branch and always hard-error.
        issues.push({
          code: 'LOCAL_STORAGE_PATH',
          severity: 'warning',
          message: `${truncate(d.value, 80)} will be used at runtime unless ${d.guardedBy} is set — cannot verify whether the platform injects it (${d.hint})`,
          fix: `Set ${d.guardedBy} in your env file or the environment's stored vars. If a managed database injects it, you can ignore this.`,
        });
      } else {
        // Full env picture: local env file + stored vars + managed names.
        // The guard var is genuinely absent, so the local fallback WILL be
        // used at runtime — trustworthy hard error.
        const autofix = dbAutofixFor(d.guardedBy);
        // Only recommend `mastra env db create` when we recognize the guard
        // var as belonging to a managed provider we can actually provision.
        // Suggesting the command for arbitrary vars (e.g. MY_CUSTOM_DB_URL)
        // would tell users to spin up infra that can't inject their var.
        const envVarFix = `Set ${d.guardedBy} in your env file or the environment's stored vars`;
        issues.push({
          code: 'LOCAL_STORAGE_PATH',
          severity: 'error',
          message: `${truncate(d.value, 80)} will be used at runtime because ${d.guardedBy} is not set (${d.hint})`,
          fix:
            autofix && SELF_SERVE_DB_KINDS.has(autofix.provider)
              ? [
                  `Run \`${dbCreateCommandFor(d.guardedBy, environmentName)}\` to attach a managed database`,
                  `Or ${envVarFix.charAt(0).toLowerCase()}${envVarFix.slice(1)}`,
                ]
              : envVarFix,
          autofix,
        });
      }
    } else if (hasEnvFile) {
      const autofix = dbAutofixFor(d.guardedBy);
      const envVarFix = `Set ${d.guardedBy} in your env file`;
      const platformFix = `If the platform already injects it, re-run with --skip-preflight`;
      issues.push({
        code: 'LOCAL_STORAGE_PATH',
        severity: 'error',
        message: `${truncate(d.value, 80)} will be used at runtime because ${d.guardedBy} is not set (${d.hint})`,
        fix:
          autofix && SELF_SERVE_DB_KINDS.has(autofix.provider)
            ? [
                `Run \`${dbCreateCommandFor(d.guardedBy, environmentName)}\` to attach a managed database`,
                `Or ${envVarFix.charAt(0).toLowerCase()}${envVarFix.slice(1)}`,
                platformFix,
              ]
            : [envVarFix, platformFix],
        autofix,
      });
    } else {
      issues.push({
        code: 'LOCAL_STORAGE_PATH',
        severity: 'warning',
        message: `${truncate(d.value, 80)} will be used at runtime unless ${d.guardedBy} is set — cannot verify ${d.guardedBy} is set on the platform (${d.hint})`,
        fix: `Ensure ${d.guardedBy} is set in the target environment, or pass --env-file so preflight can verify it locally.`,
      });
    }
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}
